import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Plus, Search, PackageCheck, PackageX, AlertCircle, Pencil, Trash2, Eye,
  Factory, History, ArrowUpCircle, ArrowDownCircle, RefreshCw, Users, FolderOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getCatalogItems, deleteCatalogItem, adjustStock, getStockTransactions } from '../lib/api';
import { getProductTypeName } from '@/lib/constants';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatApiError } from '@/lib/validators';

import { Screen, ScreenSplit, Pane, CommandBar, StatStrip, DataGrid, CellButton, SidePane } from '@/components/data';
import { useIsMobile } from '@/hooks/useIsMobile';
import PhoneCatalogList from '@/pages/Catalog/PhoneCatalogList';

/**
 * The catalog as a list, with the customer folders beside it.
 *
 * A three-across card grid at ~240px a card showed nine products for about sixty
 * characters of information each, and the "grouped" view was a second rendering
 * of the whole page rather than a filter. Products are rows now; the customer
 * folders are a rail that filters them.
 *
 * Stock adjustment, stock history and produce-for-stock were three hand-rolled
 * modal overlays — raw fixed divs, no focus handling, no Escape. They are one
 * side pane with three sections, so you can still see the product you are
 * adjusting while you adjust it.
 *
 * Below lg the same state draws one card per product with the folders as chips
 * and the stock forms in a sheet at the thumb — see PhoneCatalogList.
 */

const STOCK_FILTERS = [
  ['in_stock', 'In stock'],
  ['low_stock', 'Low'],
  ['out_of_stock', 'Out'],
];

const stockState = (item) => {
  const stock = item.finished_goods_stock || 0;
  const threshold = item.min_stock_threshold || 0;
  if (stock === 0) return 'out_of_stock';
  if (stock <= threshold) return 'low_stock';
  return 'in_stock';
};

const STOCK_TEXT = {
  in_stock: 'text-emerald-600',
  low_stock: 'text-amber-600',
  out_of_stock: 'text-red-600',
};

const STOCK_ICON = {
  in_stock: PackageCheck,
  low_stock: AlertCircle,
  out_of_stock: PackageX,
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return dateStr; }
};

export default function ProductCatalogPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState({ general: { items: [] }, customers: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerFilter, setCustomerFilter] = useState(null);
  const [stockFilter, setStockFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });

  const [pane, setPane] = useState(null); // { item, mode: 'stock' | 'produce' }
  const [stockForm, setStockForm] = useState({ type: 'add', quantity: '', reason: 'manual_adjustment', notes: '' });
  const [produceForm, setProduceForm] = useState({ quantity: '', remarks: '', priority: 'low', needed_by: '' });
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const userRoles = user?.roles || [user?.role];
  const canManageStock = userRoles.some(r => ['admin', 'receptionist', 'store_keeper'].includes(r));
  const canViewStock = userRoles.some(r => ['admin', 'receptionist', 'supervisor', 'store_keeper'].includes(r));

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: pagination.page, limit: pagination.limit };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (stockFilter) params.stock_status = stockFilter;
      if (customerFilter) params.customer_id = customerFilter;
      const data = await getCatalogItems(params);
      setItems(data.items || []);
      setPagination(prev => ({ ...prev, total: data.total || 0 }));
    } catch {
      toast.error('Failed to load catalog items');
    }
    setLoading(false);
  }, [pagination.page, pagination.limit, searchTerm, statusFilter, stockFilter, customerFilter]);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await api.get('/catalog/items/grouped', { params: { status: statusFilter || undefined } });
      setGroups(res.data || { general: { items: [] }, customers: [] });
    } catch { /* the rail just shows nothing */ }
  }, [statusFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const openStock = async (item) => {
    setPane({ item, mode: 'stock' });
    setStockForm({ type: 'add', quantity: '', reason: 'manual_adjustment', notes: '' });
    setTransactionsLoading(true);
    try {
      const data = await getStockTransactions({ catalog_item_id: item.id, limit: 50 });
      setTransactions(data.transactions || []);
    } catch {
      setTransactions([]);
    }
    setTransactionsLoading(false);
  };

  const openProduce = (item) => {
    setPane({ item, mode: 'produce' });
    setProduceForm({ quantity: '', remarks: '', priority: 'low', needed_by: '' });
  };

  const submitStock = async () => {
    if (!stockForm.quantity || parseInt(stockForm.quantity) <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    setSubmitting(true);
    try {
      await adjustStock({
        catalog_item_id: pane.item.id,
        adjustment_type: stockForm.type,
        quantity: parseInt(stockForm.quantity),
        reason: stockForm.reason,
        notes: stockForm.notes,
      });
      toast.success(`Stock ${stockForm.type === 'add' ? 'added' : 'reduced'}`);
      setPane(null);
      fetchItems();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to adjust stock'));
    }
    setSubmitting(false);
  };

  const submitProduce = async () => {
    if (!produceForm.quantity || parseInt(produceForm.quantity) <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/catalog/items/${pane.item.id}/produce-for-stock`, {
        quantity: parseInt(produceForm.quantity),
        remarks: produceForm.remarks,
        priority: produceForm.priority,
        needed_by: produceForm.needed_by,
      });
      toast.success(`Production order created: ${res.data.order_id}`);
      setPane(null);
      fetchItems();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to produce for stock'));
    }
    setSubmitting(false);
  };

  const doDelete = async () => {
    const item = confirmDelete;
    setConfirmDelete(null);
    try {
      await deleteCatalogItem(item.id);
      toast.success('Catalog item deleted');
      fetchItems();
      fetchGroups();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message || 'Failed to delete item');
    }
  };

  const counts = useMemo(() => ({
    in_stock: items.filter(i => stockState(i) === 'in_stock').length,
    low_stock: items.filter(i => stockState(i) === 'low_stock').length,
    out_of_stock: items.filter(i => stockState(i) === 'out_of_stock').length,
  }), [items]);

  const columns = useMemo(() => ([
    {
      key: 'name',
      header: 'Product',
      width: 'minmax(14rem,1.7fr)',
      render: (item) => (
        <>
          <span className="block truncate font-semibold text-slate-900">{item.name}</span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">
            {getProductTypeName(item.product_type, true)}
          </span>
        </>
      ),
    },
    {
      key: 'customer_name',
      header: 'Customer',
      width: 'minmax(9rem,1fr)',
      render: (item) => item.customer_name
        ? <span className="block truncate text-[12px] text-slate-600">{item.customer_name}</span>
        : <span className="text-[12px] text-slate-400">General</span>,
    },
    canViewStock && {
      key: 'finished_goods_stock',
      header: 'Stock',
      align: 'right',
      width: '6.5rem',
      render: (item) => {
        const state = stockState(item);
        const Icon = STOCK_ICON[state];
        return (
          <span className={`inline-flex items-center gap-1 font-semibold ${STOCK_TEXT[state]}`}>
            <Icon className="h-3 w-3" />
            {(item.finished_goods_stock || 0).toLocaleString()}
          </span>
        );
      },
      total: (rows) => rows.reduce((s, i) => s + (i.finished_goods_stock || 0), 0).toLocaleString(),
    },
    canViewStock && {
      key: 'min_stock_threshold',
      header: 'Minimum',
      align: 'right',
      width: '6.5rem',
      render: (item) => <span className="text-slate-500">{item.min_stock_threshold || '—'}</span>,
    },
    {
      key: 'last_price',
      header: 'Last price',
      align: 'right',
      width: '7.5rem',
      render: (item) => {
        const price = item.last_price ?? item.default_price ?? item.customer_price;
        return <span className="font-medium text-slate-700">{price ? `₹${Number(price).toLocaleString()}` : '—'}</span>;
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '6rem',
      render: (item) => (
        <span className={`text-[12px] font-medium ${item.status === 'active' ? 'text-emerald-600' : 'text-slate-400'}`}>
          {item.status || 'active'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '10rem',
      align: 'right',
      render: (item) => (
        <span className="flex items-center justify-end gap-0.5">
          {canManageStock && (
            <>
              <CellButton icon={History} label="Stock and history" iconOnly onClick={() => openStock(item)} />
              <CellButton icon={Factory} label="Produce for stock" iconOnly onClick={() => openProduce(item)} />
            </>
          )}
          <CellButton icon={Eye} label="Open item" iconOnly onClick={() => navigate(`/catalog/${item.id}`)} />
          <CellButton icon={Pencil} label="Edit item" iconOnly onClick={() => navigate(`/catalog/${item.id}/edit`)} />
          <CellButton icon={Trash2} label="Delete item" iconOnly tone="danger" onClick={() => setConfirmDelete(item)} />
        </span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]).filter(Boolean), [canViewStock, canManageStock, navigate]);

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const firstPage = () => setPagination(p => ({ ...p, page: 1 }));

  const deleteDialog = (
    <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete “{confirmDelete?.name}”?</AlertDialogTitle>
          <AlertDialogDescription>
            The product is removed from the catalog. Orders already placed against it are unaffected.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={doDelete} className="bg-red-600 text-white hover:bg-red-700">
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // one card per product below lg; the same state, the same confirmation
  if (isMobile) {
    return (
      <>
        <PhoneCatalogList
          items={items} total={pagination.total} loading={loading}
          page={pagination.page} totalPages={totalPages}
          onPage={(page) => setPagination(p => ({ ...p, page }))}
          onSearch={(q) => { if (q === searchTerm) return; setSearchTerm(q); firstPage(); }}
          statusFilter={statusFilter} onStatus={(v) => { setStatusFilter(v); firstPage(); }}
          stockFilter={stockFilter} counts={counts} stockState={stockState}
          onStockFilter={(key) => { setStockFilter(key); firstPage(); }}
          folders={groups.customers || []} customerFilter={customerFilter}
          onFolder={(id) => { setCustomerFilter(id); firstPage(); }}
          canViewStock={canViewStock} canManageStock={canManageStock}
          onNew={() => navigate('/catalog/new')}
          onOpen={(item) => navigate(`/catalog/${item.id}`)}
          onEdit={(item) => navigate(`/catalog/${item.id}/edit`)}
          onDelete={setConfirmDelete}
          onOpenStock={openStock} onOpenProduce={openProduce}
          pane={pane} onClosePane={() => setPane(null)}
          stockForm={stockForm} setStockForm={setStockForm}
          produceForm={produceForm} setProduceForm={setProduceForm}
          transactions={transactions} transactionsLoading={transactionsLoading}
          submitting={submitting} onSubmitStock={submitStock} onSubmitProduce={submitProduce}
        />
        {deleteDialog}
      </>
    );
  }

  return (
    <Screen data-testid="product-catalog-page">
      <CommandBar
        title="Product Catalog"
        subtitle={`${pagination.total} product${pagination.total === 1 ? '' : 's'}`}
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: () => { fetchItems(); fetchGroups(); }, disabled: loading },
          { key: 'new', label: 'New Product', icon: Plus, tone: 'accent', onClick: () => navigate('/catalog/new') },
        ]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-7 pl-8 text-xs"
            placeholder="Product name, code…"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={[
          { key: null, label: 'products', value: pagination.total, tone: 'blue' },
          canViewStock && { key: 'in_stock', label: 'In stock', value: counts.in_stock, tone: 'good' },
          canViewStock && { key: 'low_stock', label: 'Low', value: counts.low_stock, tone: counts.low_stock ? 'warn' : 'default' },
          canViewStock && { key: 'out_of_stock', label: 'Out', value: counts.out_of_stock, tone: counts.out_of_stock ? 'danger' : 'default' },
        ].filter(Boolean)}
        activeKey={stockFilter}
        onSelect={(key) => {
          setStockFilter(STOCK_FILTERS.some(([v]) => v === key) ? key : null);
          setPagination(p => ({ ...p, page: 1 }));
        }}
      >
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPagination(p => ({ ...p, page: 1 })); }}>
          <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active" className="text-xs">Active</SelectItem>
            <SelectItem value="inactive" className="text-xs">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </StatStrip>

      <ScreenSplit
        railWidth="15rem"
        rail={
          <Pane title="Folders" aside={`${(groups.customers?.length || 0) + 1}`} className="max-h-full">
            <button
              type="button"
              onClick={() => { setCustomerFilter(null); setPagination(p => ({ ...p, page: 1 })); }}
              aria-pressed={customerFilter === null}
              className={`flex w-full items-center gap-2 border-b border-slate-100 px-2.5 py-1.5 text-left text-[13px] transition-colors ${
                customerFilter === null ? 'bg-brand-blue/10 font-semibold text-brand-blue' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <FolderOpen className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 truncate">All products</span>
              <span className="shrink-0 text-[10px] text-slate-400">{pagination.total}</span>
            </button>

            {groups.general?.items?.length > 0 && (
              <div className="flex items-center gap-2 border-b border-slate-100 px-2.5 py-1.5 text-[12px] text-slate-500">
                <Package className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="flex-1 truncate">General (no customer)</span>
                <span className="shrink-0 text-[10px] text-slate-400">{groups.general.items.length}</span>
              </div>
            )}

            {(groups.customers || []).map(folder => {
              const id = folder.customer_id || folder.id;
              const on = customerFilter === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setCustomerFilter(on ? null : id); setPagination(p => ({ ...p, page: 1 })); }}
                  aria-pressed={on}
                  className={`flex w-full items-center gap-2 border-b border-slate-100 px-2.5 py-1.5 text-left text-[13px] transition-colors last:border-b-0 ${
                    on ? 'bg-brand-blue/10 font-semibold text-brand-blue' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Users className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="flex-1 truncate">{folder.customer_name || 'Unnamed'}</span>
                  <span className="shrink-0 text-[10px] text-slate-400">{folder.items?.length || 0}</span>
                </button>
              );
            })}
          </Pane>
        }
      >
        <Pane
          title="Products"
          aside={customerFilter
            ? groups.customers?.find(c => (c.customer_id || c.id) === customerFilter)?.customer_name
            : 'all folders'}
          scroll={false}
          bodyClassName="flex flex-col"
          actions={totalPages > 1 && (
            <span className="flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-blue-100">
              <button type="button" disabled={pagination.page <= 1}
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Prev</button>
              {pagination.page} / {totalPages}
              <button type="button" disabled={pagination.page >= totalPages}
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Next</button>
            </span>
          )}
        >
          <DataGrid
            data-testid="catalog-table"
            columns={columns}
            rows={items}
            loading={loading}
            getRowId={(item) => item.id}
            onRowClick={(item) => navigate(`/catalog/${item.id}`)}
            showTotals={canViewStock && items.length > 0}
            empty={
              <span className="flex flex-col items-center gap-2 text-slate-400">
                <Package className="h-8 w-8 text-slate-300" />
                No products match these filters
              </span>
            }
          />
        </Pane>
      </ScreenSplit>

      <SidePane
        open={!!pane}
        onClose={() => setPane(null)}
        title={pane?.mode === 'produce' ? 'Produce for stock' : 'Stock'}
        subtitle={pane?.item?.name}
        width="28rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setPane(null)}>Cancel</Button>
            <Button
              size="sm"
              disabled={submitting}
              onClick={pane?.mode === 'produce' ? submitProduce : submitStock}
              className="bg-brand-blue text-white hover:bg-brand-blue-dark"
            >
              {submitting ? 'Saving…' : pane?.mode === 'produce' ? 'Create production order' : 'Record adjustment'}
            </Button>
          </>
        }
      >
        {pane?.mode === 'produce' ? (
          <div className="space-y-2.5 p-3">
            <p className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-[11px] text-slate-600">
              Current stock <strong>{(pane.item.finished_goods_stock || 0).toLocaleString()}</strong>
              {pane.item.min_stock_threshold ? ` · minimum ${pane.item.min_stock_threshold.toLocaleString()}` : ''}
            </p>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Quantity to produce</span>
              <Input type="number" className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={produceForm.quantity}
                onChange={e => setProduceForm(f => ({ ...f, quantity: e.target.value }))} />
            </label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <label className="block">
                <span className="text-[11px] font-medium text-slate-600">Priority</span>
                <Select value={produceForm.priority}
                  onValueChange={v => setProduceForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low" className="text-xs">Low</SelectItem>
                    <SelectItem value="medium" className="text-xs">Medium</SelectItem>
                    <SelectItem value="high" className="text-xs">High</SelectItem>
                  </SelectContent>
                </Select>
              </label>
              <label className="block">
                <span className="text-[11px] font-medium text-slate-600">Needed by</span>
                <Input type="date" className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={produceForm.needed_by}
                  onChange={e => setProduceForm(f => ({ ...f, needed_by: e.target.value }))} />
              </label>
            </div>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Remarks</span>
              <Textarea className="mt-0.5 resize-none text-sm" rows={2} value={produceForm.remarks}
                onChange={e => setProduceForm(f => ({ ...f, remarks: e.target.value }))} />
            </label>
            <p className="text-[11px] text-slate-500">
              This raises an internal order that runs through production like any other job and
              ends at Stock In rather than dispatch — the goods go on the shelf until a PO
              releases them. It waits behind customer work unless you raise its priority.
            </p>
          </div>
        ) : pane && (
          <div className="space-y-3 p-3">
            <div className="rounded border border-slate-200">
              <p className="border-b border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Adjust
              </p>
              <div className="space-y-2 p-2">
                <div className="flex gap-1">
                  {[['add', 'Add', ArrowUpCircle], ['subtract', 'Reduce', ArrowDownCircle]].map(([value, label, Icon]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setStockForm(f => ({ ...f, type: value }))}
                      aria-pressed={stockForm.type === value}
                      className={`flex flex-1 items-center justify-center gap-1 rounded border px-2 py-1 text-[12px] font-medium transition-colors ${
                        stockForm.type === value
                          ? value === 'add'
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                            : 'border-red-300 bg-red-50 text-red-700'
                          : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" /> {label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-[11px] font-medium text-slate-600">Quantity</span>
                    <Input type="number" className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={stockForm.quantity}
                      onChange={e => setStockForm(f => ({ ...f, quantity: e.target.value }))} />
                  </label>
                  <label className="block">
                    <span className="text-[11px] font-medium text-slate-600">Reason</span>
                    <Select value={stockForm.reason} onValueChange={v => setStockForm(f => ({ ...f, reason: v }))}>
                      <SelectTrigger className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual_adjustment" className="text-xs">Manual adjustment</SelectItem>
                        <SelectItem value="damage" className="text-xs">Damage</SelectItem>
                        <SelectItem value="return" className="text-xs">Return</SelectItem>
                        <SelectItem value="stock_count" className="text-xs">Stock count</SelectItem>
                      </SelectContent>
                    </Select>
                  </label>
                </div>
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-600">Notes</span>
                  <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={stockForm.notes}
                    onChange={e => setStockForm(f => ({ ...f, notes: e.target.value }))} />
                </label>
                {stockForm.quantity > 0 && (
                  <p className="text-[11px] text-slate-500">
                    {(pane.item.finished_goods_stock || 0).toLocaleString()} →{' '}
                    <strong>
                      {(
                        (pane.item.finished_goods_stock || 0) +
                        (stockForm.type === 'add' ? 1 : -1) * (parseInt(stockForm.quantity) || 0)
                      ).toLocaleString()}
                    </strong>
                  </p>
                )}
              </div>
            </div>

            <div className="rounded border border-slate-200">
              <p className="border-b border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                History
              </p>
              {transactionsLoading ? (
                <p className="p-3 text-center text-xs text-slate-400">Loading…</p>
              ) : transactions.length === 0 ? (
                <p className="p-3 text-center text-xs text-slate-400">No movements recorded</p>
              ) : (
                <div className="max-h-64 overflow-y-auto">
                  {transactions.map((t, i) => (
                    <div key={t.id || i} className="flex items-start justify-between gap-2 border-b border-slate-100 px-2 py-1.5 last:border-b-0">
                      <span className="min-w-0">
                        <span className="block truncate text-[11px] text-slate-600">
                          {t.reason?.replace(/_/g, ' ') || t.type}
                        </span>
                        <span className="block truncate text-[10px] text-slate-400">
                          {t.created_by_name || t.recorded_by_name || '—'} · {formatDate(t.created_at || t.date)}
                        </span>
                      </span>
                      <span className={`shrink-0 text-[12px] font-bold tabular-nums ${
                        (t.adjustment_type || t.type) === 'add' ? 'text-emerald-600' : 'text-red-500'
                      }`}>
                        {(t.adjustment_type || t.type) === 'add' ? '+' : '−'}{t.quantity}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SidePane>

      {deleteDialog}
    </Screen>
  );
}
